/**
 * Input sanitization utilities for user-provided data.
 * These functions clean and normalize user input to prevent:
 * - XSS attacks (via display of unsanitized strings)
 * - Unicode spoofing/homograph attacks
 * - Whitespace manipulation
 * - Control character injection
 */
/**
 * Characters that are safe for display in player names.
 * Allows: letters, numbers, spaces, hyphens, underscores, apostrophes
 */
const SAFE_NAME_CHARS = /^[\p{L}\p{N}\s\-_']+$/u;
/**
 * Control characters and other problematic Unicode ranges to strip.
 * Includes: control chars, zero-width chars, directional overrides, etc.
 */
const UNSAFE_CHARS = 
// eslint-disable-next-line no-control-regex
/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202F\uFEFF\uFFF0-\uFFFF]/g;
/**
 * Sanitizes a player name for safe storage and display.
 *
 * - Trims leading/trailing whitespace
 * - Normalizes Unicode (NFC form)
 * - Removes control characters and zero-width characters
 * - Collapses multiple spaces into one
 * - Limits length
 *
 * @param name - Raw player name input
 * @param maxLength - Maximum allowed length (default: 12)
 * @returns Sanitized player name
 */
export function sanitizePlayerName(name, maxLength = 12) {
    return (name
        // Normalize Unicode to composed form (NFC)
        .normalize('NFC')
        // Remove control characters and zero-width chars
        .replace(UNSAFE_CHARS, '')
        // Trim whitespace
        .trim()
        // Collapse multiple spaces into one
        .replace(/\s+/g, ' ')
        // Limit length
        .slice(0, maxLength));
}
/**
 * Validates if a player name contains only safe characters.
 * Call this AFTER sanitization to check if the result is valid.
 *
 * @param name - Sanitized player name
 * @returns true if name contains only safe characters
 */
export function isValidPlayerName(name) {
    if (name.length === 0)
        return false;
    return SAFE_NAME_CHARS.test(name);
}
/**
 * Sanitizes a host name (more permissive than player names).
 *
 * @param name - Raw host name input
 * @param maxLength - Maximum allowed length (default: 32)
 * @returns Sanitized host name
 */
export function sanitizeHostName(name, maxLength = 32) {
    return name
        .normalize('NFC')
        .replace(UNSAFE_CHARS, '')
        .trim()
        .replace(/\s+/g, ' ')
        .slice(0, maxLength);
}
/**
 * Sanitizes text for safe display (e.g., chat messages, custom answers).
 * More permissive than names - allows most printable characters.
 *
 * @param text - Raw text input
 * @param maxLength - Maximum allowed length
 * @returns Sanitized text
 */
export function sanitizeDisplayText(text, maxLength = 500) {
    return text.normalize('NFC').replace(UNSAFE_CHARS, '').trim().slice(0, maxLength);
}
/**
 * Escapes HTML special characters to prevent XSS.
 * Use this when inserting user content into HTML contexts.
 *
 * @param text - Text to escape
 * @returns HTML-escaped text
 */
export function escapeHtml(text) {
    const htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;',
    };
    return text.replace(/[&<>"'/]/g, (char) => htmlEscapes[char] ?? char);
}
/**
 * Normalizes a room code for consistent lookup.
 * - Converts to uppercase
 * - Maps commonly confused characters (0->O removed since O is excluded)
 * - Removes non-letter characters
 *
 * @param code - Raw room code input
 * @returns Normalized room code
 */
export function normalizeRoomCode(code) {
    return (code
        .toUpperCase()
        // Map commonly confused characters to valid alphabet chars
        // Note: CODE_ALPHABET excludes I, O, L so map to nearest valid chars
        .replace(/0/g, 'Q') // 0 looks like O, but O excluded, use Q
        .replace(/1/g, 'J') // 1 looks like I/L, both excluded, use J
        .replace(/I/g, 'J') // I excluded, use J
        .replace(/L/g, 'J') // L excluded, use J
        .replace(/O/g, 'Q') // O excluded, use Q
        // Remove anything that's not A-Z
        .replace(/[^A-Z]/g, '')
        .slice(0, 6));
}
/**
 * Validates a room code matches the expected format.
 * Room codes use alphabet: ABCDEFGHJKMNPQRSTUVWXYZ (excludes I, O, L)
 *
 * @param code - Room code to validate
 * @returns true if valid room code format
 */
export function isValidRoomCode(code) {
    // Must be exactly 6 characters from the valid alphabet (excludes I, O, L)
    return /^[A-HJ-NP-Z]{6}$/.test(code);
}
/**
 * Maximum allowed avatar string length.
 * Avatars are short identifiers (e.g., "cat", "elephant") not base64 images.
 */
export const MAX_AVATAR_LENGTH = 16;
/**
 * Sanitizes an avatar string (emoji or short text identifier).
 * Avatars should be a single emoji or very short text.
 *
 * @param avatar - Raw avatar input
 * @param maxLength - Maximum allowed length (default: MAX_AVATAR_LENGTH)
 * @returns Sanitized avatar string
 */
export function sanitizeAvatar(avatar, maxLength = MAX_AVATAR_LENGTH) {
    return avatar.normalize('NFC').replace(UNSAFE_CHARS, '').trim().slice(0, maxLength);
}
/**
 * Maximum length for optional reason fields (kick, close room, etc.)
 */
export const MAX_REASON_LENGTH = 200;
/**
 * Sanitizes a reason string (e.g., kick reason, close room reason).
 *
 * @param reason - Raw reason input
 * @param maxLength - Maximum allowed length (default: MAX_REASON_LENGTH)
 * @returns Sanitized reason string
 */
export function sanitizeReason(reason, maxLength = MAX_REASON_LENGTH) {
    return reason.normalize('NFC').replace(UNSAFE_CHARS, '').trim().slice(0, maxLength);
}
/**
 * Maximum message size in bytes (512KB).
 * Tank Showdown can send large state updates (~200KB) with terrain data.
 * Messages larger than this should be rejected.
 */
export const MAX_MESSAGE_SIZE = 512 * 1024;
/**
 * Checks if a message payload exceeds the size limit.
 *
 * @param data - Message data (string or Buffer)
 * @returns true if message is too large
 */
export function isMessageTooLarge(data) {
    const size = typeof data === 'string' ? Buffer.byteLength(data, 'utf8') : data.length;
    return size > MAX_MESSAGE_SIZE;
}
//# sourceMappingURL=sanitize.js.map